import type { PositionBasis } from '../../lib/closing';
import { formatMoney } from '../../lib/format';

/**
 * Event Financial Performance: the deal-wide figures, under the ticket chart.
 *
 * WHY IT SITS HERE AND NOT BESIDE THE LP PANELS. These are the EVENT's numbers, derived
 * directly from what sold, so they belong next to the tier breakdown they come from. The LP
 * panels below answer a different question, which is what any of it means for this holder.
 *
 * THE FEE CHAIN IS SHOWN IN FULL, not summarised as one net figure. An investor whose
 * return is computed from net proceeds is entitled to see how gross became net, and each
 * line is a different party taking a different cut on a different basis:
 *
 *   card processing   2.9% of gross, plus 30 cents PER ORDER, so the order count is shown
 *   platform fee      5% of gross
 *   venue share       a percentage of what is left AFTER the two above, and only where the
 *                     event was published under an arrangement
 *
 * The venue line appears only when there is one. A permanent "Venue revenue share, $0.00"
 * on the thousands of events with no arrangement would invite the question of which venue
 * is taking nothing.
 */

function Line({ label, value, sub, deduction = false, total = false }: {
  label: string; value: string; sub?: string; deduction?: boolean; total?: boolean;
}) {
  return (
    <div className={`evfin__row${total ? ' evfin__row--total' : ''}`}>
      <div className="evfin__label">
        {label}
        {sub && <span className="evfin__sub">{sub}</span>}
      </div>
      <div className={`evfin__value mono${deduction ? ' evfin__value--deduction' : ''}`}>
        {deduction ? '−' : ''}{value}
      </div>
    </div>
  );
}

export function EventFinancials({ basis }: { basis: PositionBasis }) {
  const b = basis;
  const hasVenue = b.venueRevenueShareCents > 0;
  const venuePct = ((b.venueRevenueShareRate ?? 0) * 100).toFixed(
    ((b.venueRevenueShareRate ?? 0) * 100) % 1 ? 2 : 0);
  const carryPct = (b.gpCarryRate * 100).toFixed((b.gpCarryRate * 100) % 1 ? 1 : 0);
  const hurdlePct = (b.preferredReturnRate * 100).toFixed(
    (b.preferredReturnRate * 100) % 1 ? 1 : 0);

  return (
    <section className="vp-panel">
      <h3 className="vp-gradient-heading pos-title">Event Financial Performance</h3>

      <div className="evfin">
        <Line label="Gross ticket revenue" value={formatMoney(b.grossTicketRevenueCents)} />
        <Line
          label="Card processing"
          sub={`2.9% plus $0.30 on each of ${b.completedOrders.toLocaleString('en-US')} orders`}
          value={formatMoney(b.stripeProcessingFeeCents)}
          deduction
        />
        <Line
          label="Voleto platform fee"
          sub="5% of gross"
          value={formatMoney(b.voletoPlatformFeeCents)}
          deduction
        />
        {hasVenue && (
          <Line
            label={`Venue revenue share (${venuePct}%)`}
            sub="of gross less card processing and the platform fee"
            value={formatMoney(b.venueRevenueShareCents)}
            deduction
          />
        )}
        {/* Expenses the RAISE DID NOT PAY. Shown only when there are any.
            The funded production budget is deliberately NOT a line here: the raise IS that
            budget, investors supplied it, and return of capital below is where it is
            accounted for. Deducting it here as well charged the same cost twice and turned
            a profitable show into a loss on screen. */}
        {b.unfundedExpensesCents > 0 && (
          <Line
            label="Expenses not covered by the raise"
            sub="settlement costs, taxes, post-event expenses"
            value={formatMoney(b.unfundedExpensesCents)}
            deduction
          />
        )}
        <Line label="Net event cash" value={formatMoney(b.netEventCashCents)} total />
      </div>

      {/* The waterfall at the DEAL level. Each holder's own share is in the panel below;
          these are the pool totals every holding is measured against.

          RETURN OF CAPITAL IS WHERE THE PRODUCTION BUDGET IS ACCOUNTED FOR. The raise
          funded the production, so returning it here is the production cost being settled,
          not a separate line above. */}
      <h4 className="evfin__heading">Distribution waterfall</h4>
      <div className="evfin evfin--tiers">
        <Line label="1. Return of capital" value={formatMoney(b.poolReturnOfCapitalCents)} />
        <Line label={`2. Preferred return (${hurdlePct}%)`}
          value={formatMoney(b.poolPreferredReturnCents)} />
        <Line label="3. Profit above hurdle" value={formatMoney(b.profitAboveHurdleCents)} />
        <Line label={`Manager promote (${carryPct}%)`} value={formatMoney(b.gpFeeCents)} />
        <Line label={`Investor profit share (${(100 - Number(carryPct)).toFixed(
          Number(carryPct) % 1 ? 1 : 0)}%)`} value={formatMoney(b.investorPoolCents)} />
      </div>

      <p className="pos-note">
        Deal-wide totals across every holding in this raise. Your own share of each is in
        Your Financial Performance below. The capital returned in tier 1 is the production
        budget this raise funded, which is why it is not also deducted above.
      </p>
    </section>
  );
}
